import React, { useState, useMemo } from 'react';
import { FaGlobe, FaBars, FaTags, FaUtensils, FaLayerGroup, FaSearch } from 'react-icons/fa';
import { Form } from 'react-bootstrap';
import SearchDropdown from '../../../../../../../../../../components/common/SearchDropdown/SearchDropdown';
import './DiscountReach.scss';

interface DiscountReachProps {
  applyOn: string;
  setApplyOn: (applyOn: string) => void;
  menuOptions: any[];
  categoryOptions: any[];
  itemOptions: any[];
  menuId: string;
  setMenuId: (menuId: string) => void;
  categoryId: string;
  setCategoryId: (categoryId: string) => void;
  itemId: string;
  setItemId: (itemId: string) => void;
  showAlcanceExplanation: string;
  setShowAlcanceExplanation: (explanation: string) => void;
  selectedCategory: any;
  selectedItem: any;
  // New multi-select props
  itemIds?: number[];
  setItemIds?: (itemIds: number[]) => void;
  categoryIds?: number[];
  setCategoryIds?: (categoryIds: number[]) => void;
}

const DiscountReach: React.FC<DiscountReachProps> = ({
  applyOn,
  setApplyOn,
  menuOptions,
  categoryOptions,
  itemOptions,
  menuId,
  setMenuId,
  categoryId,
  setCategoryId,
  itemId,
  setItemId,
  showAlcanceExplanation,
  setShowAlcanceExplanation,
  selectedCategory,
  selectedItem,
  itemIds = [],
  setItemIds,
  categoryIds = [],
  setCategoryIds
}) => {
  
  // Search state for multi-select
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  
  // Multi-select handlers
  const handleItemToggle = (id: number) => {
    if (!setItemIds) return;
    if (itemIds.includes(id)) {
      setItemIds(itemIds.filter(itemId => itemId !== id));
    } else {
      setItemIds([...itemIds, id]);
    }
  };

  const handleCategoryToggle = (id: number) => {
    if (!setCategoryIds) return;
    if (categoryIds.includes(id)) {
      setCategoryIds(categoryIds.filter(catId => catId !== id));
    } else {
      setCategoryIds([...categoryIds, id]);
    }
  };

  const selectedItems = itemOptions.filter(item => itemIds.includes(item.id));
  const selectedCategories = categoryOptions.filter(cat => categoryIds.includes(cat.id));

  // Filtered items based on search
  const filteredItems = useMemo(() => {
    if (!itemSearchTerm.trim()) return itemOptions;
    return itemOptions.filter(item =>
      item.name.toLowerCase().includes(itemSearchTerm.toLowerCase())
    );
  }, [itemOptions, itemSearchTerm]);

  // Filtered categories based on search
  const filteredCategories = useMemo(() => {
    if (!categorySearchTerm.trim()) return categoryOptions;
    return categoryOptions.filter(cat =>
      cat.name.toLowerCase().includes(categorySearchTerm.toLowerCase())
    );
  }, [categoryOptions, categorySearchTerm]);

  return (
    <div className="form-section apply-section discount-reach-section">
      <div className="section-title">
        <FaGlobe className="section-icon" />
        Alcance del descuento
      </div>

      {showAlcanceExplanation && (
        <div className="alcance-explanation-banner">
          <div className="explanation-content">
            {showAlcanceExplanation === 'Global' && 'Se aplica a toda la compra del cliente'}
            {showAlcanceExplanation === 'Menú' && 'Solo se aplica a un menú en particular'}
            {showAlcanceExplanation === 'Categoría' && 'Solo para productos de una categoría específica'}
            {showAlcanceExplanation === 'Item' && 'Solo para un producto en particular'}
            {showAlcanceExplanation === 'Multi-Item' && 'Se aplica a múltiples productos seleccionados'}
            {showAlcanceExplanation === 'Multi-Categoría' && 'Se aplica a múltiples categorías seleccionadas'}
          </div>
          <button
            className="close-explanation"
            onClick={() => setShowAlcanceExplanation('')}
          >
            ×
          </button>
        </div>
      )}

      <div className="apply-options multi-row">
        <div
          className={`apply-option compact ${applyOn === 'Global' ? 'active' : ''}`}
          onClick={() => {
            setApplyOn('Global');
            setCategoryId('');
            setMenuId('');
            setItemId('');
            if (setItemIds) setItemIds([]);
            if (setCategoryIds) setCategoryIds([]);
            setShowAlcanceExplanation(showAlcanceExplanation === 'Global' ? '' : 'Global');
          }}
        >
          <div className="apply-icon"><FaGlobe /></div>
          <div className="apply-title">Global</div>
        </div>

        {menuOptions && menuOptions.length > 1 && (
          <div
            className={`apply-option compact ${applyOn === 'Menú' ? 'active' : ''}`}
            onClick={() => {
              setApplyOn('Menú');
              setCategoryId('');
              setItemId('');
              if (setItemIds) setItemIds([]);
              if (setCategoryIds) setCategoryIds([]);
              setShowAlcanceExplanation(showAlcanceExplanation === 'Menú' ? '' : 'Menú');
            }}
          >
            <div className="apply-icon"><FaBars /></div>
            <div className="apply-title">Menú</div>
          </div>
        )}

        <div
          className={`apply-option compact ${applyOn === 'Categoría' ? 'active' : ''}`}
          onClick={() => {
            setApplyOn('Categoría');
            setMenuId('');
            setItemId('');
            if (setItemIds) setItemIds([]);
            if (setCategoryIds) setCategoryIds([]);
            setShowAlcanceExplanation(showAlcanceExplanation === 'Categoría' ? '' : 'Categoría');
          }}
        >
          <div className="apply-icon"><FaTags /></div>
          <div className="apply-title">Categoría</div>
        </div>

        <div
          className={`apply-option compact ${applyOn === 'Item' ? 'active' : ''}`}
          onClick={() => {
            setApplyOn('Item');
            setMenuId('');
            setCategoryId('');
            if (setItemIds) setItemIds([]);
            if (setCategoryIds) setCategoryIds([]);
            setShowAlcanceExplanation(showAlcanceExplanation === 'Item' ? '' : 'Item');
          }}
        >
          <div className="apply-icon"><FaUtensils /></div>
          <div className="apply-title">Ítem</div>
        </div>

        {/* New Multi-Item option */}
        <div
          className={`apply-option compact ${applyOn === 'Multi-Item' ? 'active' : ''}`}
          onClick={() => {
            setApplyOn('Multi-Item');
            setMenuId('');
            setCategoryId('');
            setItemId('');
            if (setCategoryIds) setCategoryIds([]);
            setShowAlcanceExplanation(showAlcanceExplanation === 'Multi-Item' ? '' : 'Multi-Item');
          }}
        >
          <div className="apply-icon"><FaLayerGroup /></div>
          <div className="apply-title">Multi-Ítem</div>
        </div>

        {/* New Multi-Category option */}
        <div
          className={`apply-option compact ${applyOn === 'Multi-Categoría' ? 'active' : ''}`}
          onClick={() => {
            setApplyOn('Multi-Categoría');
            setMenuId('');
            setCategoryId('');
            setItemId('');
            if (setItemIds) setItemIds([]);
            setShowAlcanceExplanation(showAlcanceExplanation === 'Multi-Categoría' ? '' : 'Multi-Categoría');
          }}
        >
          <div className="apply-icon"><FaLayerGroup /></div>
          <div className="apply-title">Multi-Categoría</div>
        </div>
      </div>

      {/* Selection dropdowns */}
      <div className="apply-selection">
        {applyOn === 'Menú' && menuOptions && menuOptions.length > 1 && (
          <div className="menu-selection">
            <div className="menu-list">
              {menuOptions.map((menu, index) => (
                <div
                  key={menu.id}
                  className={`menu-option ${menuId === menu.id || (index === 0 && !menuId) ? 'active' : ''}`}
                  onClick={() => setMenuId(menu.id)}
                >
                  <div className="menu-name">{menu.name}</div>
                  {(index === 0 && !menuId) && <div className="default-badge">Por defecto</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {applyOn === 'Categoría' && (
          <div className="search-dropdown high-z-index">
            <SearchDropdown
              items={categoryOptions}
              selected={selectedCategory}
              setSelected={(selectedCat: any) => setCategoryId(selectedCat.id)}
              displayProperty="name"
              idProperty="id"
              placeholder="Selecciona una categoría"
              dropdownClassName="mb-3 high-z-dropdown"
            />
          </div>
        )}

        {applyOn === 'Item' && (
          <div className="search-dropdown high-z-index">
            <SearchDropdown
              items={itemOptions}
              selected={selectedItem}
              setSelected={(selectedItm: any) => setItemId(selectedItm.id)}
              displayProperty="name"
              idProperty="id"
              placeholder="Selecciona un ítem"
              dropdownClassName="mb-3 high-z-dropdown"
            />
          </div>
        )}

        {/* Multi-Item Selection */}
        {applyOn === 'Multi-Item' && (
          <div className="multi-select-container">
            <div className="multi-select-header">
              Selecciona múltiples ítems ({itemIds.length} seleccionados)
            </div>

            {/* Selected items badges - displayed at the top */}
            {selectedItems.length > 0 && (
              <div className="selected-items-badges">
                <div className="badges-title">Seleccionados:</div>
                <div className="selected-tags">
                  {selectedItems.map(item => (
                    <span key={item.id} className="selected-tag">
                      {item.name}
                      <button
                        className="remove-tag"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleItemToggle(item.id);
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Search bar */}
            <div className="search-bar-container">
              <div className="search-input-wrapper">
                <Form.Control
                  type="text"
                  placeholder="Buscar ítems..."
                  value={itemSearchTerm}
                  onChange={(e) => setItemSearchTerm(e.target.value)}
                  className="multi-select-search"
                />
                {itemSearchTerm && (
                  <button
                    className="clear-search"
                    onClick={() => setItemSearchTerm('')}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* Scrollable list */}
            <div className="multi-select-list">
              {filteredItems.length > 0 ? (
                filteredItems.map(item => (
                  <div
                    key={item.id}
                    className={`multi-select-item ${itemIds.includes(item.id) ? 'selected' : ''}`}
                    onClick={() => handleItemToggle(item.id)}
                  >
                    <input
                      type="checkbox"
                      checked={itemIds.includes(item.id)}
                      onChange={() => handleItemToggle(item.id)}
                      className="multi-select-checkbox"
                    />
                    <span className="multi-select-label">{item.name}</span>
                  </div>
                ))
              ) : (
                <div className="no-results">
                  No se encontraron ítems que coincidan con "{itemSearchTerm}"
                </div>
              )}
            </div>
          </div>
        )}

        {/* Multi-Category Selection */}
        {applyOn === 'Multi-Categoría' && (
          <div className="multi-select-container">
            <div className="multi-select-header">
              Selecciona múltiples categorías ({categoryIds.length} seleccionadas)
            </div>

            {/* Selected categories badges - displayed at the top */}
            {selectedCategories.length > 0 && (
              <div className="selected-items-badges">
                <div className="badges-title">Seleccionadas:</div>
                <div className="selected-tags">
                  {selectedCategories.map(category => (
                    <span key={category.id} className="selected-tag">
                      {category.name}
                      <button
                        className="remove-tag"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCategoryToggle(category.id);
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Search bar */}
            <div className="search-bar-container">
              <div className="search-input-wrapper">
                <Form.Control
                  type="text"
                  placeholder="Buscar categorías..."
                  value={categorySearchTerm}
                  onChange={(e) => setCategorySearchTerm(e.target.value)}
                  className="multi-select-search"
                />
                {categorySearchTerm && (
                  <button
                    className="clear-search"
                    onClick={() => setCategorySearchTerm('')}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* Scrollable list */}
            <div className="multi-select-list">
              {filteredCategories.length > 0 ? (
                filteredCategories.map(category => (
                  <div
                    key={category.id}
                    className={`multi-select-item ${categoryIds.includes(category.id) ? 'selected' : ''}`}
                    onClick={() => handleCategoryToggle(category.id)}
                  >
                    <input
                      type="checkbox"
                      checked={categoryIds.includes(category.id)}
                      onChange={() => handleCategoryToggle(category.id)}
                      className="multi-select-checkbox"
                    />
                    <span className="multi-select-label">{category.name}</span>
                  </div>
                ))
              ) : (
                <div className="no-results">
                  No se encontraron categorías que coincidan con "{categorySearchTerm}"
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscountReach;